import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { DatePicker, Dropdown, Switch, TextField } from 'basic-components';
import { DataStorageEnums, DataStorageReadableConsts } from 'dashboard-services/api/dataStorage';
import { CommonService } from 'dashboard-services/services';

import properties from 'resources/constants/properties.json';
import { FormulaUtils } from 'utils';

import { When } from 'react-if';

import './DataRange.scss';

const DataRange = ({ onChange, from = undefined, fromSelected = undefined, to = undefined, toSelected = undefined, range = undefined, lastType = undefined, lastTypeAmount = undefined, withAdditionalToday = false, fromOverridden = false, toOverridden = false, rangeOverridden = false, lastTypeOverridden = false, lastTypeAmountOverridden = false }) => {
  const onChangeNumber = useCallback((val, { name }) => {
          if(!CommonService.isNumber(val)) {
            return;
          }
          const valFor = Number(val) < 0 ? Number(val) * -1 : val
          onChange(valFor, { name })
        }, [onChange]),
        onChangeFrom = useCallback(val => {
          const localVal = val === "" ? undefined : val
          onChange(localVal, { name: "from" })
          onChange(DataStorageEnums.DATE_BETWEEN_TYPE.DATE, { name: "fromSelected" })
          if(toSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE && (!to || to?.length < 10)) {
            onChange(localVal, { name: "to" })
            onChange(DataStorageEnums.DATE_BETWEEN_TYPE.DATE, { name: "toSelected" })
          }
        }, [onChange, to, toSelected]),
        onChangeTo = useCallback(val => {
          const localVal = val === "" ? undefined : val
          onChange(localVal, { name: "to" })
          onChange(DataStorageEnums.DATE_BETWEEN_TYPE.DATE, { name: "toSelected" })
          if(fromSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE && (!from || from?.length < 10)) {
            onChange(localVal, { name: "from" })
            onChange(DataStorageEnums.DATE_BETWEEN_TYPE.DATE, { name: "fromSelected" })
          }
        }, [from, fromSelected, onChange]),
        onChangeToday = useCallback((val, { name }) => onChange(val ? DataStorageEnums.DATE_BETWEEN_TYPE.TODAY : DataStorageEnums.DATE_BETWEEN_TYPE.DATE, { name }), [onChange]),
        tsDataRangesLocal = useMemo(() => withAdditionalToday ? DataStorageReadableConsts.DATA_RANGES.concat([{ label: "Today", value: "TODAY" }]) : DataStorageReadableConsts.DATA_RANGES, [withAdditionalToday]),
        defaultRange = useMemo(() => tsDataRangesLocal.find(d => d.value === range), [range, tsDataRangesLocal]),
        tsLastTypesFilterd = useMemo(() => {
          if(properties.warehouse_only !== "true") {
            return DataStorageReadableConsts.LAST_TYPES.filter(t => !t.warehouseOnly)
          } else {
            return DataStorageReadableConsts.LAST_TYPES
          }
        }, []),
        defaultLastType = useMemo(() => tsLastTypesFilterd.find(t => t.value === lastType), [lastType, tsLastTypesFilterd])

  return (
    <div className="data-range">
      <div className="data-range__row data-range__row--is-full">
        <div
            className="data-range__row__label"
            id="date-range-label"
        >
          Data range type
        </div>
        <div className="data-range__row__value">
          <Dropdown
              defaultValue={rangeOverridden ? { label: FormulaUtils.OVERRIDDEN, value: FormulaUtils.OVERRIDDEN } : defaultRange}
              disabled={rangeOverridden}
              fixed
              labelId="date-range-label"
              menuPlacement="top"
              menuPortalTarget={document.querySelector('.ng-office-app')}
              name="range"
              noError
              onChange={onChange}
              options={tsDataRangesLocal}
              placeholder="Select data range"
              variant="border"
          />
        </div>
      </div>
      <When condition={range === DataStorageEnums.DATA_RANGE.LAST || range === DataStorageEnums.DATA_RANGE.NEXT}>
        {() => (
          <Fragment>
            <div className="data-range__row">
              <div
                  className="data-range__row__label"
                  id="type-label"
              >
                Type
              </div>
              <div className="data-range__row__value">
                <Dropdown
                    defaultValue={lastTypeOverridden ? { label: FormulaUtils.OVERRIDDEN, value: FormulaUtils.OVERRIDDEN } : defaultLastType}
                    disabled={lastTypeOverridden}
                    fixed
                    labelId="type-label"
                    menuPortalTarget={document.querySelector('.ng-office-app')}
                    name="lastType"
                    noError
                    onChange={onChange}
                    options={tsLastTypesFilterd}
                    variant="border"
                />
              </div>
            </div>
            <div className="data-range__row">
              <div className="data-range__row__label">
                Amount
              </div>
              <div className="data-range__row__value">
                <TextField
                    aria-label={`${lastType} type amount`}
                    defaultValue={lastTypeAmountOverridden ? FormulaUtils.OVERRIDDEN  : lastTypeAmount}
                    disabled={lastTypeAmountOverridden}
                    min={lastTypeAmountOverridden ? undefined : 1}
                    name="lastTypeAmount"
                    noError
                    onChange={onChangeNumber}
                    type={lastTypeAmountOverridden ? "text" : "number"}
                    variant="border"
                />
              </div>
            </div>
          </Fragment>
        )}
      </When>
      <When condition={range === DataStorageEnums.DATA_RANGE.BETWEEN}>
        {() => (
          <Fragment>
            <div className="data-range__row">
              <div className="data-range__row__label">
                From
              </div>
              <div className="data-range__row__value">
                <DatePicker
                    defaultValue={fromOverridden ? FormulaUtils.OVERRIDDEN : DataStorageEnums.DATE_BETWEEN_TYPE.TODAY === fromSelected ? "Today" : from}
                    disabled={fromOverridden || DataStorageEnums.DATE_BETWEEN_TYPE.TODAY === fromSelected}
                    isPortal
                    name="from"
                    noError
                    onChange={onChangeFrom}
                    onClickOpen
                    variant="border"
                    zIndex="99"
                />
              </div>
              <div className="data-range__row__today">
                <p>Today</p>
                <Switch
                    disabled={fromOverridden}
                    name="fromSelected"
                    noError
                    on={DataStorageEnums.DATE_BETWEEN_TYPE.TODAY === fromSelected}
                    onChange={onChangeToday}
                />
              </div>
            </div>
            <div className="data-range__row">
              <div className="data-range__row__label">
                To
              </div>
              <div className="data-range__row__value">
                <DatePicker
                    defaultValue={toOverridden ? FormulaUtils.OVERRIDDEN : DataStorageEnums.DATE_BETWEEN_TYPE.TODAY === toSelected ? "Today" : to}
                    disabled={toOverridden || DataStorageEnums.DATE_BETWEEN_TYPE.TODAY === toSelected}
                    isPortal
                    name="to"
                    noError
                    onChange={onChangeTo}
                    onClickOpen
                    variant="border"
                    zIndex="99"
                />
              </div>
              <div className="data-range__row__today">
                <p>Today</p>
                <Switch
                    disabled={toOverridden}
                    name="toSelected"
                    noError
                    on={DataStorageEnums.DATE_BETWEEN_TYPE.TODAY === toSelected}
                    onChange={onChangeToday}
                />
              </div>
            </div>
          </Fragment>
        )}
      </When>
    </div>
  )
}

DataRange.propTypes = {
  from: PropTypes.string,
  fromOverridden: PropTypes.bool,
  fromSelected: PropTypes.string,
  lastType: PropTypes.string,
  lastTypeAmount: PropTypes.string,
  lastTypeAmountOverridden: PropTypes.bool,
  lastTypeOverridden: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  range: PropTypes.string,
  rangeOverridden: PropTypes.bool,
  to: PropTypes.string,
  toOverridden: PropTypes.bool,
  toSelected: PropTypes.string,
  withAdditionalToday: PropTypes.bool,
}

export default DataRange;
